import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Card, Button, Form } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import Table from '../../../../components/Table';
import {
    addDCCircuitAPI,
    deleteDCCircuitAPI,
    editDCCircuitAPI,
    getAllVendorsAPI,
    getMasterDcCircuitAPI,
    getStationNameAPI,
    registerDeviceMobius,
    startAndStopAlertAPI,
} from '../../../../API/RDPMS_API';
import moment from 'moment';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useSelector } from 'react-redux';
import { capitalizeFirstLetter } from '../../../../constants/Uppercase';
import { addDCCircuitColumns, sizePerPageList } from '../../../../utils/ColumnUtils';
import { capitalize } from '../../../../utils/statusUtils';
import EnableDisableButtons from '../../../../components/Buttons/EnableDisableButtons';
import '../../../../../src/assets/scss/custom/components/_addDcCircuit.scss';
import * as bootstrap from 'bootstrap';
import AddMasterModal from '../../../../components/modal/AddMasterModal';
import EditMasterModal from '../../../../components/modal/EditMasterModal';
import DeleteMasterModal from '../../../../components/modal/DeleteMasterModal';

const AddDcCircuit = () => {
    const { t } = useTranslation();

    //  States
    const [loader, setLoader] = useState(false);
    const [data, setData] = useState([]);
    const [vendor, setVendor] = useState([]);
    const [stationName, setStationName] = useState([]);
    const [limit, setLimit] = useState(5);
    const [pageNumber, setPageNumber] = useState(1);
    const [pageCount, setPageCount] = useState(1);
    const [totalCount, setTotalCount] = useState(0);

    const [showModal, setShowModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showEditModalData, setShowEditModalData] = useState({});
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [circuitToDelete, setCircuitToDelete] = useState(null);
    const [selectedToDeleteDeviceId, setSelectedToDeleteDeviceId] = useState(null);

    //  Auth Info
    const { auth } = useSelector((state) => {
        const user = state.Auth.user?._j || state.Auth.user;
        return { auth: { ...state.Auth, user } };
    });

    const stationIds = auth?.user?.stationId || '';
    const userId = auth?.user?._id || '';
    const userRole = capitalizeFirstLetter(auth?.user?.role || 'Admin');

    //  Fetch all master data
    const fetchAllData = useCallback(async () => {
        try {
            setLoader(true);
            await Promise.all([getMasterDcCircuitData(limit, pageNumber), getStationNameData(), getAllVendorsData()]);
        } catch (error) {
            console.error('Error fetching all data:', error);
        } finally {
            setLoader(false);
        }
    }, [limit, pageNumber]);

    useEffect(() => {
        fetchAllData();
    }, [fetchAllData]);

    //  Vendor Data
    const getAllVendorsData = async () => {
        try {
            const response = await getAllVendorsAPI();
            if (Array.isArray(response) && response.length) setVendor(response);
        } catch (error) {
            console.error('Vendor fetch error:', error);
            toast.error(t('Failed to refresh vendor data'), { position: 'top-right' });
        }
    };

    //  Station Data
    const getStationNameData = async () => {
        try {
            const response = await getStationNameAPI({ userId });
            if (Array.isArray(response) && response.length) setStationName(response);
        } catch (error) {
            console.error('Station fetch error:', error);
        }
    };

    //  Master DC Circuit Data
    const getMasterDcCircuitData = async (limits = limit, pages = pageNumber, showNoDataPopup = true) => {
        try {
            const request = { limit: limits, page: pages, stationId: stationIds };
            const response = await getMasterDcCircuitAPI(request);

            if (response?.data?.length) {
                const formattedData = response.data.map((item) => ({
                    id: item._id,
                    createdDate: moment(item?.updatedAt).format('Do MMM YYYY, h:mm a'),
                    trackNumber: item.track_number,
                    assetTypeCode: item.asset_type_code,
                    assetNumberCode: item.asset_number_code,
                    smmsAssetCode: item.smms_asset_code,
                    smmsAssetName: item.smms_asset_name,
                    channel: item.channel,
                    device: item.device_id,
                    type: capitalize(item.type),
                    stationMasterName: capitalize(item?.stationDetails?.[0]?.station_master),
                    stationName: item?.stationDetails?.[0]?.name,
                    vendor: item.vendorDetails?.[0]?.vendor_cloud_name || '',
                    city: item?.stationDetails?.[0]?.city,
                    state: item?.stationDetails?.[0]?.state,
                    parameterTypeId: item.parameter_type_id,
                    action: (
                        <div className="d-flex justify-content-center align-items-center gap-3">
                            <Link to="#" className="action-icon" onClick={() => openEditModalWithHeaderClass(item)}>
                                <i className="mdi mdi-pencil"></i>
                            </Link>
                            <Link
                                to="#"
                                className="action-icon text-danger"
                                onClick={() => {
                                    setSelectedToDeleteDeviceId(item.device_id);
                                    openDeleteModal(item._id);
                                }}>
                                <i className="mdi mdi-delete"></i>
                            </Link>
                        </div>
                    ),
                    stopAlert: (
                        <Form.Check
                            type="switch"
                            id={`custom-switch-${item._id}`}
                            checked={item.alert_status}
                            onChange={(e) => handleToggle(e, item._id)}
                        />
                    ),
                }));

                setData(formattedData);
                setPageNumber(response.currentPage || 1);
                setPageCount(response.totalPages || 1);
                setTotalCount(response.totalCount || 0);
                new bootstrap.Modal(document.getElementById('updateDcCircuit')).show();
            } else if (showNoDataPopup) {
                const modalInstance = new bootstrap.Modal(document.getElementById('alertNoDataModal'));
                modalInstance.show();
                setTimeout(() => modalInstance.hide(), 1000);
            }
        } catch (error) {
            console.error('DC Circuit fetch error:', error);
            toast.error(t('Failed to fetch DC circuit data'), { position: 'top-right' });
        }
    };

    //  Alert Toggle (Single or All)
    const handleToggle = async (e, id, isAll = false) => {
        try {
            const request = { name: 'DC Circuit', id, status: e.target.checked, isAll, stationId: stationIds };
            const response = await startAndStopAlertAPI(request);
            if (response) getMasterDcCircuitData(limit, pageNumber);
        } catch (error) {
            console.error('Toggle alert error:', error);
        }
    };

    const enableAllAlerts = () => handleToggle({ target: { checked: true } }, null, true);
    const disableAllAlerts = () => handleToggle({ target: { checked: false } }, null, true);

    //  Pagination
    const handlePageChange = (value) => {
        setLimit(value);
        setPageNumber(1);
    };
    const handlePageClick = (value) => setPageNumber(value);

    //  Modal Handlers
    const openModalWithHeaderClass = () => setShowModal(true);
    const closeModalWithHeaderClass = () => setShowModal(false);
    const openEditModalWithHeaderClass = (data) => {
        setShowEditModalData(data);
        setShowEditModal(true);
    };
    const closeEditModalWithHeaderClass = () => setShowEditModal(false);
    const openDeleteModal = (id) => {
        setCircuitToDelete(id);
        setShowDeleteModal(true);
    };
    const closeDeleteModal = () => {
        setCircuitToDelete(null);
        setShowDeleteModal(false);
    };

    //  Form Validation Schema
    const schemaResolver = yupResolver(
        yup.object({
            trackNumber: yup.string().required(t('Please Enter Track Number')),
            assetTypeCode: yup.string().required(t('Please Enter Asset Type Code')),
            assetTypeId: yup.string().required(t('Please Enter Asset Type ID')),
            smmsAssetCode: yup.string().required(t('Please Enter SMMS Asset Code')),
            smmsAssetName: yup.string().required(t('Please Enter SMMS Asset Name')),
            assetNumberCode: yup.string().required(t('Please Enter Asset Number Code')),
            assetNumberId: yup.string().required(t('Please Enter Asset Number ID')),
            parameterTypeId: yup.string().required(t('Please Enter Parameter ID')),
            vendorId: yup.string().required(t('Please Select Vendor')),
            deviceId: yup.string().required(t('Please Enter Device ID')),
            channel: yup
                .number()
                .typeError(t('Please enter a valid number'))
                .required(t('Please Enter Channel Number'))
                .transform((value, originalValue) => (originalValue === '' ? undefined : value)),
            type: yup.string().required(t('Please Select Type')),
            stationId: yup.string().required(t('Please Select Station Name')),
        })
    );

    //  Submit (Add)
    const onSubmit = async (formData) => {
        try {
            setLoader(true);
            // const payload = {
            //     ...formData,
            //     type: capitalizeFirstLetter(formData.type),
            //     createdBy: userRole,
            //     updatedBy: userRole,
            //     assetTypeName: 'DC Track Circuit',
            // };
            const payload = {
                'm2m:cin': {
                    con: {
                        ...formData,
                        type: capitalizeFirstLetter(formData.type),
                        createdBy: userRole,
                        updatedBy: userRole,
                        assetTypeName: 'DC Track Circuit',
                        status: 'active',
                    },
                },
            };
            const result = await registerDeviceMobius(payload);

            if (result?.isSucess === true) {
                setTimeout(() => {
                    getMasterDcCircuitData(limit, pageNumber, false);
                }, 1000);
                setShowModal(false);
                toast.success(t('Added DC circuit successfully'), { position: 'top-right' });
            } else {
                toast.error(t('Error adding DC circuit'), { position: 'top-right' });
            }

            // const result = await addDCCircuitAPI(payload);
            // console.log(result, 'result.......');

            // if (result?.length) {
            //     getMasterDcCircuitData();
            //     setShowModal(false);
            //     toast.success(t('Added DC circuit successfully'), { position: 'top-right' });
            // } else {
            //     toast.error(t('Error adding DC circuit'), { position: 'top-right' });
            // }
        } catch (error) {
            console.error('Add error:', error);
            toast.error(t('Error adding DC circuit'), { position: 'top-right' });
        } finally {
            setLoader(false);
        }
    };

    //  Edit
    const onEdit = async (formData) => {
        try {
            setLoader(true);
            // const payload = {
            //     ...formData,
            //     id: showEditModalData._id,
            //     type: capitalizeFirstLetter(formData.type),
            //     updatedBy: userRole,
            //     assetTypeName: 'DC Track Circuit',
            // };
            // const result = await editDCCircuitAPI(payload);

            const payload = {
                'm2m:cin': {
                    con: {
                        ...formData,
                        id: showEditModalData._id,
                        type: capitalizeFirstLetter(formData.type),
                        updatedBy: userRole,
                        assetTypeName: 'DC Track Circuit',
                        status: 'active',
                    },
                },
            };
            const result = await registerDeviceMobius(payload);

            if (result?.isSucess === true) {
                setTimeout(() => {
                    getMasterDcCircuitData(limit, pageNumber, false);
                }, 1000);

                setShowEditModal(false);
                toast.success(t('Updated DC circuit successfully'), { position: 'top-right' });
            } else {
                toast.error(t('Error updating DC circuit'), { position: 'top-right' });
            }
        } catch (error) {
            console.error('Edit error:', error);
            toast.error(t('Error updating DC circuit'), { position: 'top-right' });
        } finally {
            setLoader(false);
        }
    };

    //  Delete
    const onDeleteDcCircuit = async () => {
        if (!circuitToDelete) return;
        try {
            setLoader(true);
            // const result = await deleteDCCircuitAPI(circuitToDelete);

            const payload = {
                'm2m:cin': {
                    con: {
                        id: circuitToDelete,
                        deviceId: selectedToDeleteDeviceId,
                        assetTypeName: 'DC Track Circuit',
                        status: 'inactive',
                    },
                },
            };
            const result = await registerDeviceMobius(payload);

            if (result?.isSucess === true) {
                setData((prev) => prev.filter((row) => row.id !== circuitToDelete));
                setTotalCount((prev) => prev - 1);
                closeDeleteModal();
                await getMasterDcCircuitData();
                toast.success(t('Deleted DC circuit successfully'), { position: 'top-right' });
            } else {
                toast.error(t('Failed to delete DC circuit'), { position: 'top-right' });
            }
        } catch (error) {
            console.error('Delete error:', error);
            toast.error(t('Error deleting DC circuit'), { position: 'top-right' });
        } finally {
            setLoader(false);
        }
    };

    return (
        <>
            <ToastContainer />
            <Row>
                <Col>
                    <div className="page-title-box mb-4">
                        <div className="page-title-right">
                            <form className="d-flex">
                                <div className="input-group">
                                    <Button className="export text-black" onClick={openModalWithHeaderClass}>
                                        {t('ADD DC CIRCUIT')}
                                    </Button>
                                    <button
                                        className="update"
                                        id="update"
                                        type="button"
                                        onClick={() => getMasterDcCircuitData(limit, pageNumber)}>
                                        {t('Update')} <i className="fas fa-sync-alt"></i>
                                    </button>
                                </div>
                            </form>
                        </div>
                        <div style={{ marginLeft: '-2rem' }}>
                            <button className="btn_1">{t('DC Circuit')}</button>
                        </div>
                    </div>
                </Col>
            </Row>

            <Row>
                <Col>
                    <Card>
                        <Card.Body>
                            <Col className="d-flex justify-content-end align-items-cente">
                                <EnableDisableButtons
                                    onEnableAll={enableAllAlerts}
                                    onDisableAll={disableAllAlerts}
                                    t={t}
                                />
                            </Col>
                            <div
                                className="col-6 col-sm-6 card-add-dccircuit p-3 border-0 text-start no-radius my-2"
                                style={{
                                    width: '100%',
                                    boxShadow: '0px 0px 35px 0px rgba(154, 161, 171, 0.15)',
                                    position: 'relative',
                                }}>
                                <div className="dccircuit-table-wrapper">
                                    <Table
                                        columns={addDCCircuitColumns(t)}
                                        data={data}
                                        pageSize={limit}
                                        pageCount={pageCount}
                                        pageNumber={pageNumber}
                                        totalCount={totalCount}
                                        currentPage={pageNumber}
                                        sizePerPageList={sizePerPageList}
                                        isSortable={true}
                                        pagination={true}
                                        isSearchable={true}
                                        onPageChange={handlePageChange}
                                        onPageClick={handlePageClick}
                                    />
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <AddMasterModal
                t={t}
                title="ADD DC CIRCUIT"
                showModal={showModal}
                closeModalWithHeaderClass={() => closeModalWithHeaderClass()}
                onSubmit={onSubmit}
                schemaResolver={schemaResolver}
                extraField={{
                    label: 'Track Number',
                    name: 'trackNumber',
                    type: 'text',
                    placeholder: 'Enter Track Number',
                }}
                stationName={stationName}
                vendor={vendor}
                loader={loader}
            />

            <EditMasterModal
                t={t}
                title="EDIT DC CIRCUIT"
                showModal={showEditModal}
                closeModalWithHeaderClass={() => closeEditModalWithHeaderClass()}
                onSubmit={onEdit}
                schemaResolver={schemaResolver}
                extraField={{
                    label: 'Track Number',
                    name: 'trackNumber',
                    type: 'text',
                    placeholder: 'Enter Track Number',
                    value: showEditModalData?.track_number,
                }}
                showEditModalData={showEditModalData}
                stationName={stationName}
                vendor={vendor}
                loader={loader}
            />

            <DeleteMasterModal
                t={t}
                showModal={showDeleteModal}
                closeModal={() => closeDeleteModal()}
                onDelete={onDeleteDcCircuit}
                title="Delete DC Circuit"
                message="Are you sure you want to delete this DC circuit?"
                imageSrc="/asset-images/deleteiconmodal.png"
            />

            <div
                className="modal fade"
                id="updateDcCircuit"
                tabIndex="-1"
                aria-labelledby="updateDcCircuitLabel"
                aria-hidden="true">
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-header close">
                            <button
                                type="button"
                                className="btn-close ms-auto"
                                data-bs-dismiss="modal"
                                aria-label="Close"></button>
                        </div>
                        <div className="modal-body text-center">
                            <img
                                src="/asset-images/checkmark.png"
                                alt="Success"
                                className="img-fluid"
                                style={{ maxWidth: '100px' }}
                            />
                            <p className="mt-3">{t('DC circuit data refreshed successfully')}</p>
                        </div>
                    </div>
                </div>
            </div>
            {/* No Data Modal */}
            <div
                className="modal fade"
                id="alertNoDataModal"
                tabIndex="-1"
                aria-labelledby="alertNoDataModalLabel"
                aria-hidden="true">
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-header close">
                            <button
                                type="button"
                                className="btn-close ms-auto"
                                data-bs-dismiss="modal"
                                aria-label="Close"></button>
                        </div>
                        <div className="modal-body text-center">
                            <img
                                src="/asset-images/checkmark.png"
                                alt="No Data"
                                className="img-fluid"
                                style={{ maxWidth: '100px' }}
                            />
                            <p className="mt-3">No DC circuit data available</p>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default AddDcCircuit;
